import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useOutletContext } from 'react-router-dom';

import { Button } from '../../../../components/core';
import { useMerchant } from '../../../../contexts/MerchantContext';
import type { AgentHubOutletContext } from '../../agentHubOutletContext';
import { PublishLaunchModal } from '../components/PublishLaunchModal';
import type { OnboardingDraft } from '../OnboardingDraftContext';
import { useOnboardingDraft } from '../OnboardingDraftContext';
import { GUARDRAIL_OPTIONS } from './GuardrailsStep';
import { hasReadinessWorkflowRuleText, READINESS_CHECK_ITEMS } from './ReadinessCheckStep';
import { AGENT_TEMPLATE_OPTIONS, INDUSTRY_VERTICAL_OPTIONS } from './TemplateStep';
import { getOnboardingPlatform } from '../platforms';
import { ECOMMERCE_PLATFORM_OPTIONS } from './YourStoreStep';
import type { OnboardingStepProps } from '../index';

export type PublishSummaryItem = {
  id: string;
  label: string;
  value: string;
  tone?: 'default' | 'warning';
};

const optionLabel = (
  options: readonly { id: string; title?: string; label?: string }[],
  id: string,
): string | undefined => {
  const match = options.find((option) => option.id === id);
  return match?.title ?? match?.label;
};

const getStoreSuffix = (store: OnboardingDraft['store']): string => {
  const address = getOnboardingPlatform(store.platformId).formatStoreAddress(store.shopDomain);
  return address ? ` · ${address}` : '';
};

export const buildPublishSummary = (
  draft: OnboardingDraft,
  merchantName?: string,
): PublishSummaryItem[] => {
  const items: PublishSummaryItem[] = [];

  const platformLabel = optionLabel(ECOMMERCE_PLATFORM_OPTIONS, draft.store.platformId);
  if (platformLabel) {
    const storeName = merchantName?.trim() || 'Your store';
    const shopSuffix = getStoreSuffix(draft.store);
    items.push({
      id: 'store',
      label: 'Store',
      value: `${storeName} — ${platformLabel}${shopSuffix}`,
    });
  }

  const agentName = draft.agentSetup.agentName.trim();
  if (agentName) {
    items.push({
      id: 'agent',
      label: 'Agent',
      value: agentName,
    });
  }

  const templateTitle = optionLabel(AGENT_TEMPLATE_OPTIONS, draft.template.agentTemplateId);
  const verticalTitle = optionLabel(INDUSTRY_VERTICAL_OPTIONS, draft.template.industryVerticalId);
  if (templateTitle && verticalTitle) {
    items.push({
      id: 'template',
      label: 'Template',
      value: `${templateTitle} — ${verticalTitle}`,
    });
  }

  const lockedCount = GUARDRAIL_OPTIONS.filter((option) => option.locked).length;
  const optionalActiveCount = GUARDRAIL_OPTIONS.filter(
    (option) => !option.locked && draft.guardrails[option.id],
  ).length;
  const activeCount = lockedCount + optionalActiveCount;
  items.push({
    id: 'guardrails',
    label: 'Guardrails',
    value: `${activeCount} active (${lockedCount} always on)`,
  });

  const openGaps = READINESS_CHECK_ITEMS.filter(
    (item) => !hasReadinessWorkflowRuleText(draft.rules, item.id),
  );
  if (openGaps.length > 0) {
    items.push({
      id: 'open-gaps',
      label: 'Open gaps',
      value: `${openGaps.length} warning${openGaps.length === 1 ? '' : 's'}: ${openGaps
        .map((item) => item.title)
        .join(', ')}`,
      tone: 'warning',
    });
  }

  return items;
};

export const PublishStep = ({ stepIndex, totalSteps }: OnboardingStepProps): JSX.Element => {
  const { onEnableSuccess } = useOutletContext<AgentHubOutletContext>();
  const { draft } = useOnboardingDraft();
  const { merchantName } = useMerchant();
  const summaryItems = buildPublishSummary(draft, merchantName);
  const [isPublishModalOpen, setIsPublishModalOpen] = React.useState(false);

  const handlePublish = () => {
    setIsPublishModalOpen(true);
  };

  return (
    <Box data-testid="ella-onboarding-publish-step">
      <Typography
        component="p"
        variant="small"
        color="link.main"
        fontWeight="semibold"
        sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.6px' }}
      >
        Step {stepIndex + 1} of {totalSteps}
      </Typography>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} aria-hidden />
        <Typography component="h2" variant="xxl" sx={{ mb: 1 }}>
          Save your agent configurations
        </Typography>
        <Typography variant="medium" color="text.secondary" sx={{ mb: 4, maxWidth: 520 }}>
          Agent is configured, guardrails are on, and your storefront setup is saved.
        </Typography>

        <Box
          data-testid="ella-onboarding-publish-summary"
          sx={{
            width: '100%',
            maxWidth: 520,
            px: 2.5,
            py: 2,
            mb: 4,
            border: '1px solid',
            borderColor: 'border.main',
            borderRadius: '4px',
            bgcolor: 'background.paper',
            textAlign: 'left',
          }}
        >
          <Typography
            variant="small"
            fontWeight="bold"
            sx={{ mb: 3, textTransform: 'uppercase', letterSpacing: '0.6px' }}
          >
            Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {summaryItems.map((item) => (
              <Box
                key={item.id}
                data-testid={`ella-onboarding-publish-summary-${item.id}`}
                sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
              >
                <Typography variant="medium" fontWeight="semibold" sx={{ minWidth: 88 }}>
                  {item.label}
                </Typography>
                <Typography
                  variant="medium"
                  color={item.tone === 'warning' ? 'warning.main' : 'text.secondary'}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Button
          theme="primary"
          text="Save agent"
          onClick={handlePublish}
          dataTestId="ella-onboarding-publish-agent"
          sx={{ minWidth: 200, height: 48 }}
        />
      </Box>

      <PublishLaunchModal
        open={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onPublishSuccess={onEnableSuccess}
      />
    </Box>
  );
};
